'use client'

import { Check, ChevronDown, X } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  value: string
  label: string
  color?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  value?: string[]
  onValueChange?: (value: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  dropdownPosition?: 'top' | 'bottom'
  emptyState?: React.ReactNode | string
}

const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      value = [],
      onValueChange,
      placeholder,
      className,
      disabled,
      dropdownPosition = 'auto',
      emptyState,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const [shouldOpenUpward, setShouldOpenUpward] = React.useState(false)
    const containerRef = React.useRef<HTMLDivElement>(null)

    React.useEffect(() => {
      if (isOpen && containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const dropdownHeight = 240 // max-h-60 = 240px
        const padding = 20 // Extra padding for better UX
        const spaceBelow = window.innerHeight - rect.bottom - padding
        const spaceAbove = rect.top - padding

        if (dropdownPosition === 'top') {
          setShouldOpenUpward(true)
        } else if (dropdownPosition === 'bottom') {
          setShouldOpenUpward(false)
        } else {
          if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            setShouldOpenUpward(true)
          } else {
            setShouldOpenUpward(false)
          }
        }
      }
    }, [isOpen, dropdownPosition])

    const handleSelect = (optionValue: string) => {
      const newValue = value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
      onValueChange?.(newValue)
    }

    const handleRemove = (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation()
      const newValue = value.filter((v) => v !== optionValue)
      onValueChange?.(newValue)
    }

    const selectedOptions = value
      .map((v) => options.find((o) => o.value === v))
      .filter(Boolean) as MultiSelectOption[]

    return (
      <div ref={containerRef} className={cn('relative', className)} {...props}>
        <div
          ref={ref}
          className={cn(
            'border-input flex min-h-9 w-full items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-sm focus:shadow-[0_0_0_0.2rem_hsl(var(--primary)_/_0.3)] focus:outline-none focus:outline-offset-0 disabled:cursor-not-allowed disabled:opacity-50',
            isOpen && 'focus:shadow-[0_0_0_0.2rem_hsl(var(--primary)_/_0.3)]',
            disabled && 'cursor-not-allowed opacity-50'
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div className="flex flex-1 flex-wrap gap-1">
            {selectedOptions.length > 0 ? (
              selectedOptions.map((option) => (
                <div
                  key={option.value}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs"
                  style={{
                    backgroundColor: option.color || 'hsl(var(--secondary))',
                    color: option.color ? '#ffffff' : undefined,
                  }}
                >
                  {option.label}
                  <button
                    type="button"
                    className="hover:opacity-80"
                    style={{ color: option.color ? '#ffffff' : undefined }}
                    onClick={(e) => handleRemove(option.value, e)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder || 'Select options...'}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </div>

        {isOpen && (
          <div
            className={cn(
              'bg-popover text-popover-foreground absolute z-[100] max-h-60 min-w-[8rem] overflow-y-auto rounded-md border bg-white p-1 shadow-md',
              shouldOpenUpward || dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
            )}
          >
            {options.length === 0 ? (
              <div className="text-muted-foreground px-2 py-3 text-center text-sm">
                {typeof emptyState === 'string'
                  ? emptyState
                  : emptyState
                    ? emptyState
                    : 'Keine Optionen verfügbar'}
              </div>
            ) : (
              options.map((option) => {
                const isSelected = value.includes(option.value)
                return (
                  <div
                    key={option.value}
                    className={cn(
                      'hover:bg-accent hover:text-accent-foreground relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none',
                      isSelected && 'bg-accent text-accent-foreground'
                    )}
                    onClick={() => handleSelect(option.value)}
                  >
                    {option.color && (
                      <span
                        className="mr-2 h-3 w-3 rounded-full"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
                      {isSelected && <Check className="h-4 w-4" />}
                    </span>
                    {option.label}
                  </div>
                )
              })
            )}
          </div>
        )}

        {/* Overlay to close dropdown when clicking outside */}
        {isOpen && <div className="fixed inset-0 z-[90]" onClick={() => setIsOpen(false)} />}
      </div>
    )
  }
)

MultiSelect.displayName = 'MultiSelect'

export { MultiSelect }
